import React from 'react';
import { Check, HelpCircle } from 'lucide-react';

interface FormData {
  selectedProviders: string[];
  providerNumbers: Record<string, string>;
  cardMapping: Record<string, string>;
}

interface TerminalRequestFormStep4Props {
  form: FormData;
  handleChange: (e: any) => void;
}

export const TerminalRequestFormStep4: React.FC<TerminalRequestFormStep4Props> = ({
  form,
  handleChange
}) => {
  const getSupportedProviders = (cardType: string): string[] => {
    switch (cardType) {
      case 'visa':
      case 'mastercard':
        return form.selectedProviders.filter(p => ['כאל', 'ישראכרט', 'MAX'].includes(p));
      case 'isracard':
        return form.selectedProviders.filter(p => ['ישראכרט', 'MAX'].includes(p));
      case 'amex':
        return form.selectedProviders.filter(p => p === 'ישראכרט');
      case 'diners':
        return form.selectedProviders.filter(p => p === 'כאל');
      default:
        return [];
    }
  };

  React.useEffect(() => {
    if (form.selectedProviders && form.selectedProviders.length > 0) {
      const newMapping = { ...form.cardMapping };
      ['visa', 'mastercard', 'isracard', 'amex', 'diners'].forEach(cardType => {
        const supported = getSupportedProviders(cardType);
        if (supported.length === 0) {
          newMapping[cardType] = 'לא נתמך';
        } else if (!newMapping[cardType] || newMapping[cardType] === 'לא נתמך') {
          newMapping[cardType] = '';
        }
      });
      handleChange({ target: { name: 'cardMapping', value: newMapping } } as any);
    }
  }, [form.selectedProviders]);

  return (
    <div className="space-y-4 animate-fadeIn pb-8">
      <div className="text-center space-y-2 mb-5">
        <div className="w-14 h-14 bg-gradient-to-br from-pink-100 to-pink-200 rounded-2xl flex items-center justify-center mx-auto mb-2 shadow-sm">
          <div className="text-2xl">💳</div>
        </div>
        <h2 className="text-xl font-bold text-gray-900">חברת סליקה</h2>
        <p className="text-sm text-gray-500">בחר את חברות הסליקה עימן אתה עובד</p>
      </div>

      {/* שלב 1 - בחירת חברות סליקה */}
      <div className="space-y-2.5">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold shadow-sm">1</div>
          <h3 className="text-base font-semibold text-gray-900">בחר חברת סליקה</h3>
        </div>
        <p className="text-xs text-gray-600 mb-2.5 leading-relaxed">בחר את החברות שדרכן אתה מבצע סליקה</p>

        {/* כאל */}
        <label
          className={`block p-3.5 rounded-2xl cursor-pointer transition-all active:scale-[0.97] shadow-sm ${
            form.selectedProviders?.includes('כאל')
              ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-200'
              : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <span className={`text-base font-semibold block mb-0.5 ${
                form.selectedProviders?.includes('כאל') ? 'text-white' : 'text-gray-900'
              }`}>
                כאל (ויזה כאל)
              </span>
              <span className={`text-xs leading-tight ${
                form.selectedProviders?.includes('כאל') ? 'text-blue-50/90' : 'text-gray-500'
              }`}>
                תומכת ב-Visa, MasterCard, Isracard, Diners
              </span>
            </div>
            <input
              type="checkbox"
              name="selectedProviders"
              value="כאל"
              checked={form.selectedProviders?.includes('כאל') || false}
              onChange={handleChange}
              className="sr-only"
            />
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
              form.selectedProviders?.includes('כאל')
                ? 'bg-white shadow-sm'
                : 'bg-gray-50 border-2 border-gray-300'
            }`}>
              {form.selectedProviders?.includes('כאל') && (
                <Check className="w-4 h-4 text-blue-500" strokeWidth={3} />
              )}
            </div>
          </div>
        </label>

        {/* MAX */}
        <label
          className={`block p-3.5 rounded-2xl cursor-pointer transition-all active:scale-[0.97] shadow-sm ${
            form.selectedProviders?.includes('MAX')
              ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-200'
              : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <span className={`text-base font-semibold block mb-0.5 ${
                form.selectedProviders?.includes('MAX') ? 'text-white' : 'text-gray-900'
              }`}>
                MAX (לאומיכארד)
              </span>
              <span className={`text-xs leading-tight ${
                form.selectedProviders?.includes('MAX') ? 'text-blue-50/90' : 'text-gray-500'
              }`}>
                תומכת ב-Visa, MasterCard, Isracard
              </span>
            </div>
            <input
              type="checkbox"
              name="selectedProviders"
              value="MAX"
              checked={form.selectedProviders?.includes('MAX') || false}
              onChange={handleChange}
              className="sr-only"
            />
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
              form.selectedProviders?.includes('MAX')
                ? 'bg-white shadow-sm'
                : 'bg-gray-50 border-2 border-gray-300'
            }`}>
              {form.selectedProviders?.includes('MAX') && (
                <Check className="w-4 h-4 text-blue-500" strokeWidth={3} />
              )}
            </div>
          </div>
        </label>

        {/* ישראכרט */}
        <label
          className={`block p-3.5 rounded-2xl cursor-pointer transition-all active:scale-[0.97] shadow-sm ${
            form.selectedProviders?.includes('ישראכרט')
              ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-200'
              : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <span className={`text-base font-semibold block mb-0.5 ${
                form.selectedProviders?.includes('ישראכרט') ? 'text-white' : 'text-gray-900'
              }`}>
                ישראכרט
              </span>
              <span className={`text-xs leading-tight ${
                form.selectedProviders?.includes('ישראכרט') ? 'text-blue-50/90' : 'text-gray-500'
              }`}>
                תומכת ב-Visa, MasterCard, Isracard, Amex
              </span>
            </div>
            <input
              type="checkbox"
              name="selectedProviders"
              value="ישראכרט"
              checked={form.selectedProviders?.includes('ישראכרט') || false}
              onChange={handleChange}
              className="sr-only"
            />
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
              form.selectedProviders?.includes('ישראכרט')
                ? 'bg-white shadow-sm'
                : 'bg-gray-50 border-2 border-gray-300'
            }`}>
              {form.selectedProviders?.includes('ישראכרט') && (
                <Check className="w-4 h-4 text-blue-500" strokeWidth={3} />
              )}
            </div>
          </div>
        </label>

        {(!form.selectedProviders || form.selectedProviders.length === 0) && (
          <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-2.5 mt-2">
            <p className="text-xs text-amber-800 text-center font-medium">
              <strong>שים לב:</strong> יש לבחור לפחות חברה אחת
            </p>
          </div>
        )}
      </div>

      {/* שלב 2 - פרטי מספר ספק */}
      {form.selectedProviders && form.selectedProviders.length > 0 && (
        <div className="space-y-2.5 pt-4">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold shadow-sm">2</div>
            <h3 className="text-base font-semibold text-gray-900">פרטי מספר ספק</h3>
          </div>

          {form.selectedProviders.includes('כאל') && (
            <div className="bg-gradient-to-br from-blue-50 to-blue-50/30 rounded-2xl p-3.5 border border-blue-100 shadow-sm space-y-2.5">
              <h4 className="font-semibold text-sm text-gray-900">כאל (ויזה כאל)</h4>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  מספר ספק / מספר בית עסק <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="providerNumber-כאל"
                  value={form.providerNumbers?.['כאל'] || ''}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none shadow-sm"
                  placeholder="לדוגמה: 12345678"
                  required
                />
                {(!form.providerNumbers?.['כאל']) && (
                  <p className="text-xs text-red-500 mt-1.5 font-medium">נדרש למילוי</p>
                )}
              </div>

              <details className="group">
                <summary className="cursor-pointer text-xs text-blue-600 font-semibold hover:text-blue-700 flex items-center gap-1.5 py-1">
                  <HelpCircle className="w-3.5 h-3.5" />
                  לא יודע את המספר?
                </summary>
                <div className="mt-2 p-2.5 bg-white/80 rounded-xl text-xs text-gray-700 space-y-1.5 shadow-sm">
                  <p><strong>איפה למצוא:</strong> מופיע על חשבוניות ודוחות הסליקה</p>
                  <p><strong>אם לא נמצא:</strong> פנה לתמיכת בתי עסק של כאל</p>
                </div>
              </details>
            </div>
          )}

          {form.selectedProviders.includes('MAX') && (
            <div className="bg-gradient-to-br from-green-50 to-green-50/30 rounded-2xl p-3.5 border border-green-100 shadow-sm space-y-2.5">
              <h4 className="font-semibold text-sm text-gray-900">MAX (לאומיכארד)</h4>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  מספר ספק / מספר בית עסק <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="providerNumber-MAX"
                  value={form.providerNumbers?.['MAX'] || ''}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-all outline-none shadow-sm"
                  placeholder="לדוגמה: 12345678"
                  required
                />
                {(!form.providerNumbers?.['MAX']) && (
                  <p className="text-xs text-red-500 mt-1.5 font-medium">נדרש למילוי</p>
                )}
              </div>

              <details className="group">
                <summary className="cursor-pointer text-xs text-green-600 font-semibold hover:text-green-700 flex items-center gap-1.5 py-1">
                  <HelpCircle className="w-3.5 h-3.5" />
                  לא יודע את המספר?
                </summary>
                <div className="mt-2 p-2.5 bg-white/80 rounded-xl text-xs text-gray-700 space-y-1.5 shadow-sm">
                  <p className="font-semibold text-amber-700">⚠️ אין מספר ספק אחיד ב-MAX</p>
                  <p><strong>איפה למצוא:</strong> מופיע בדוחות או בחשבוניות של MAX</p>
                  <p><strong>אם לא נמצא:</strong> פנה לתמיכת בתי עסק של MAX (לאומיכארד)</p>
                </div>
              </details>
            </div>
          )}

          {form.selectedProviders.includes('ישראכרט') && (
            <div className="bg-gradient-to-br from-purple-50 to-purple-50/30 rounded-2xl p-3.5 border border-purple-100 shadow-sm space-y-2.5">
              <h4 className="font-semibold text-sm text-gray-900">ישראכרט</h4>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  מספר בית עסק (Merchant ID) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="providerNumber-ישראכרט"
                  value={form.providerNumbers?.['ישראכרט'] || ''}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all outline-none shadow-sm"
                  placeholder="לדוגמה: 12345678"
                  required
                />
                {(!form.providerNumbers?.['ישראכרט']) && (
                  <p className="text-xs text-red-500 mt-1.5 font-medium">נדרש למילוי</p>
                )}
              </div>

              <details className="group">
                <summary className="cursor-pointer text-xs text-purple-600 font-semibold hover:text-purple-700 flex items-center gap-1.5 py-1">
                  <HelpCircle className="w-3.5 h-3.5" />
                  לא יודע את המספר?
                </summary>
                <div className="mt-2 p-2.5 bg-white/80 rounded-xl text-xs text-gray-700 space-y-1.5 shadow-sm">
                  <p><strong>איפה למצוא:</strong> בדוחות זיכויים/חיובים או בחשבוניות</p>
                  <p><strong>אם לא נמצא:</strong> פנה לתמיכת בתי עסק של ישראכרט</p>
                </div>
              </details>
            </div>
          )}
        </div>
      )}

      {/* שלב 3 - מיפוי סוגי כרטיסים */}
      {form.selectedProviders && form.selectedProviders.length > 0 &&
       form.selectedProviders.every((p: string) => form.providerNumbers?.[p]) && (
        <div className="space-y-2.5 pt-4">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold shadow-sm">3</div>
            <h3 className="text-base font-semibold text-gray-900">מיפוי כרטיסים לסולק</h3>
          </div>

          <p className="text-xs text-gray-600 mb-2.5 leading-relaxed">
            בחר מי מסלק כל סוג כרטיס. <strong>לדוגמה:</strong> ויזה → כאל, Amex → ישראכרט
          </p>

          <div className="space-y-2">
            {/* Visa */}
            <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">Visa</span>
                <select
                  className={`px-2.5 py-1.5 bg-gray-50 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all ${
                    getSupportedProviders('visa').length === 0 || form.cardMapping?.visa === 'לא נתמך' ? 'text-gray-500' :
                    !form.cardMapping?.visa ? 'border-red-300 text-red-600' : 'border-gray-300 text-gray-700'
                  }`}
                  value={form.cardMapping?.visa || ''}
                  onChange={(e) => {
                    const newMapping = { ...form.cardMapping, visa: e.target.value };
                    handleChange({ target: { name: 'cardMapping', value: newMapping } } as any);
                  }}
                  disabled={getSupportedProviders('visa').length === 0}
                >
                  {getSupportedProviders('visa').length === 0 ? (
                    <option value="לא נתמך">לא נתמך</option>
                  ) : (
                    <>
                      <option value="">בחר...</option>
                      <option value="לא נתמך">לא נתמך</option>
                      {getSupportedProviders('visa').map((provider: string) => (
                        <option key={provider} value={provider}>{provider}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* MasterCard */}
            <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">MasterCard</span>
                <select
                  className={`px-2.5 py-1.5 bg-gray-50 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all ${
                    getSupportedProviders('mastercard').length === 0 || form.cardMapping?.mastercard === 'לא נתמך' ? 'text-gray-500' :
                    !form.cardMapping?.mastercard ? 'border-red-300 text-red-600' : 'border-gray-300 text-gray-700'
                  }`}
                  value={form.cardMapping?.mastercard || ''}
                  onChange={(e) => {
                    const newMapping = { ...form.cardMapping, mastercard: e.target.value };
                    handleChange({ target: { name: 'cardMapping', value: newMapping } } as any);
                  }}
                  disabled={getSupportedProviders('mastercard').length === 0}
                >
                  {getSupportedProviders('mastercard').length === 0 ? (
                    <option value="לא נתמך">לא נתמך</option>
                  ) : (
                    <>
                      <option value="">בחר...</option>
                      <option value="לא נתמך">לא נתמך</option>
                      {getSupportedProviders('mastercard').map((provider: string) => (
                        <option key={provider} value={provider}>{provider}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* Isracard */}
            <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">Isracard</span>
                <select
                  className={`px-2.5 py-1.5 bg-gray-50 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all ${
                    getSupportedProviders('isracard').length === 0 || form.cardMapping?.isracard === 'לא נתמך' ? 'text-gray-500' :
                    !form.cardMapping?.isracard ? 'border-red-300 text-red-600' : 'border-gray-300 text-gray-700'
                  }`}
                  value={form.cardMapping?.isracard || ''}
                  onChange={(e) => {
                    const newMapping = { ...form.cardMapping, isracard: e.target.value };
                    handleChange({ target: { name: 'cardMapping', value: newMapping } } as any);
                  }}
                  disabled={getSupportedProviders('isracard').length === 0}
                >
                  {getSupportedProviders('isracard').length === 0 ? (
                    <option value="לא נתמך">לא נתמך</option>
                  ) : (
                    <>
                      <option value="">בחר...</option>
                      <option value="לא נתמך">לא נתמך</option>
                      {getSupportedProviders('isracard').map((provider: string) => (
                        <option key={provider} value={provider}>{provider}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* Amex */}
            <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">American Express</span>
                <select
                  className={`px-2.5 py-1.5 bg-gray-50 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all ${
                    getSupportedProviders('amex').length === 0 || form.cardMapping?.amex === 'לא נתמך' ? 'text-gray-500' :
                    !form.cardMapping?.amex ? 'border-red-300 text-red-600' : 'border-gray-300 text-gray-700'
                  }`}
                  value={form.cardMapping?.amex || ''}
                  onChange={(e) => {
                    const newMapping = { ...form.cardMapping, amex: e.target.value };
                    handleChange({ target: { name: 'cardMapping', value: newMapping } } as any);
                  }}
                  disabled={getSupportedProviders('amex').length === 0}
                >
                  {getSupportedProviders('amex').length === 0 ? (
                    <option value="לא נתמך">לא נתמך</option>
                  ) : (
                    <>
                      <option value="">בחר...</option>
                      <option value="לא נתמך">לא נתמך</option>
                      {getSupportedProviders('amex').map((provider: string) => (
                        <option key={provider} value={provider}>{provider}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* Diners */}
            <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">Diners</span>
                <select
                  className={`px-2.5 py-1.5 bg-gray-50 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all ${
                    getSupportedProviders('diners').length === 0 || form.cardMapping?.diners === 'לא נתמך' ? 'text-gray-500' :
                    !form.cardMapping?.diners ? 'border-red-300 text-red-600' : 'border-gray-300 text-gray-700'
                  }`}
                  value={form.cardMapping?.diners || ''}
                  onChange={(e) => {
                    const newMapping = { ...form.cardMapping, diners: e.target.value };
                    handleChange({ target: { name: 'cardMapping', value: newMapping } } as any);
                  }}
                  disabled={getSupportedProviders('diners').length === 0}
                >
                  {getSupportedProviders('diners').length === 0 ? (
                    <option value="לא נתמך">לא נתמך</option>
                  ) : (
                    <>
                      <option value="">בחר...</option>
                      <option value="לא נתמך">לא נתמך</option>
                      {getSupportedProviders('diners').map((provider: string) => (
                        <option key={provider} value={provider}>{provider}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>

          {(() => {
            const missingCards = [];
            if (getSupportedProviders('visa').length > 0 && !form.cardMapping?.visa) missingCards.push('Visa');
            if (getSupportedProviders('mastercard').length > 0 && !form.cardMapping?.mastercard) missingCards.push('MasterCard');
            if (getSupportedProviders('isracard').length > 0 && !form.cardMapping?.isracard) missingCards.push('Isracard');
            if (getSupportedProviders('amex').length > 0 && !form.cardMapping?.amex) missingCards.push('American Express');
            if (getSupportedProviders('diners').length > 0 && !form.cardMapping?.diners) missingCards.push('Diners');

            return missingCards.length > 0 ? (
              <div className="bg-red-50/80 border-2 border-red-200 rounded-xl p-3 mt-3">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-red-800 mb-1">נדרש למלא את כל הכרטיסים הנתמכים</p>
                    <p className="text-xs text-red-700 leading-relaxed mb-2">
                      יש לבחור סולק עבור כל כרטיס נתמך. אם אין לך אחד מהם, בחר <strong>"לא בשימוש"</strong>.
                    </p>
                    <ul className="text-xs text-red-700 space-y-1">
                      {missingCards.map(card => (
                        <li key={card}>• <strong>{card}</strong> - נדרש לבחור</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ) : null;
          })()}
        </div>
      )}
    </div>
  );
};
